/* Lumetier · Onboarding (claim your studio) — dark SaaS theme */

import { useEffect, useMemo, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Sparkles, ArrowRight, Loader2, CheckCircle2, AlertTriangle } from "lucide-react";
import LumetierLogo from "@/components/LumetierLogo";

const API_BASE = process.env.REACT_APP_BACKEND_URL;
const GOLD = "#C9A961";
const GOLD_LIGHT = "#EDD78C";

function slugify(name) {
  return (name || "")
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, "")
    .trim()
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .slice(0, 40)
    .replace(/^-+|-+$/g, "");
}

export default function LumetierOnboard() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";

  const [loading, setLoading] = useState(true);
  const [invite, setInvite] = useState(null);
  const [error, setError] = useState(null);

  const [step, setStep] = useState(0);
  const [ownerName, setOwnerName] = useState("");
  const [slug, setSlug] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    async function run() {
      if (!token) {
        setError({ code: 400, message: "Missing invite token. Use the link from your email." });
        setLoading(false);
        return;
      }
      try {
        const resp = await fetch(`${API_BASE}/api/lumetier/invite/${encodeURIComponent(token)}`);
        const data = await resp.json().catch(() => ({}));
        if (!resp.ok) {
          if (!cancelled) {
            setError({ code: resp.status, message: data.detail || "Couldn't validate invite." });
          }
        } else if (!cancelled) {
          setInvite(data);
          setOwnerName(data.owner_name || "");
          setSlug(slugify(data.studio_name));
        }
      } catch (e) {
        if (!cancelled) setError({ code: 500, message: e.message || "Couldn't reach the server." });
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    run();
    return () => { cancelled = true; };
  }, [token]);

  const slugUrl = useMemo(() => slug ? `lumetier.studio/s/${slug}` : "lumetier.studio/s/your-slug", [slug]);
  const slugLooksValid = /^[a-z0-9](?:[a-z0-9-]{1,38}[a-z0-9])?$/.test(slug);

  async function handleSubmit() {
    if (!slugLooksValid) return toast.error("Slug must be 3-40 chars: lowercase letters, digits, or hyphens (no leading/trailing hyphen).");
    if (password.length < 8) return toast.error("Password must be at least 8 characters.");
    setSubmitting(true);
    try {
      const resp = await fetch(`${API_BASE}/api/lumetier/onboard`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, studio_slug: slug, password, owner_name: ownerName }),
      });
      const data = await resp.json();
      if (!resp.ok) throw new Error(data.detail || "Couldn't create studio.");
      localStorage.setItem("allure_token", data.token);
      toast.success(`Welcome to Lumetier, ${data.studio.name} ✦`);
      window.location.href = "/admin";
    } catch (err) {
      toast.error(err.message || "Couldn't create studio");
    } finally {
      setSubmitting(false);
    }
  }

  if (loading) {
    return (
      <Frame>
        <div className="flex items-center justify-center min-h-[400px] text-stone-500">
          <Loader2 className="w-5 h-5 animate-spin mr-2" /> Validating invite…
        </div>
      </Frame>
    );
  }

  if (error) {
    return (
      <Frame>
        <div className="max-w-md mx-auto text-center py-16" data-testid="onboard-error">
          <AlertTriangle className="w-10 h-10 mx-auto mb-4" style={{ color: GOLD }} />
          <h1
            className="text-3xl font-light mb-3 text-stone-100"
            style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}
          >
            Can&apos;t open this invite
          </h1>
          <p className="text-stone-400 text-sm mb-6">{error.message}</p>
          <Link to="/lumetier" className="text-xs uppercase tracking-[0.22em] text-stone-400 underline hover:text-stone-100">
            ← Back to Lumetier
          </Link>
        </div>
      </Frame>
    );
  }

  if (step === 0) {
    return (
      <Frame>
        <div className="max-w-xl mx-auto text-center py-12" data-testid="onboard-welcome">
          <div className="text-[11px] uppercase tracking-[0.32em] mb-5 inline-flex items-center gap-1.5" style={{ color: GOLD }}>
            <Sparkles className="w-3 h-3" /> Charter beta · invitation
          </div>
          <h1
            className="text-5xl font-light mb-5 text-stone-100"
            style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}
          >
            Welcome,{" "}
            <em
              className="italic not-italic"
              style={{
                backgroundImage: `linear-gradient(135deg, ${GOLD}, ${GOLD_LIGHT}, ${GOLD})`,
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
                fontStyle: "italic",
              }}
            >
              {invite.owner_name}
            </em>.
          </h1>
          <p className="text-stone-400 mb-8 max-w-md mx-auto text-sm leading-relaxed">
            You&apos;ve been invited to launch <strong className="text-stone-200">{invite.studio_name}</strong> on Lumetier.
            Three quick steps and you&apos;re in.
          </p>
          {invite.personal_note && (
            <blockquote
              className="bg-white/[0.03] text-stone-300 text-sm text-left px-5 py-4 my-6 max-w-md mx-auto"
              style={{ borderLeft: `2px solid ${GOLD}` }}
            >
              {invite.personal_note}
              {invite.invited_by_email && (
                <div className="text-xs text-stone-500 mt-2">— {invite.invited_by_email}</div>
              )}
            </blockquote>
          )}
          <button
            onClick={() => setStep(1)}
            data-testid="onboard-start-btn"
            className="inline-flex items-center gap-2 h-12 px-8 text-xs uppercase tracking-[0.22em] text-stone-900 font-medium hover:opacity-90 transition-opacity"
            style={{ background: `linear-gradient(135deg, ${GOLD}, ${GOLD_LIGHT})` }}
          >
            Let&apos;s set up your studio <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </Frame>
    );
  }

  if (step === 1) {
    return (
      <Frame>
        <Stepper step={1} />
        <div className="max-w-xl mx-auto" data-testid="onboard-slug-step">
          <h2
            className="text-3xl font-light mb-2 text-stone-100"
            style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}
          >
            Pick your URL
          </h2>
          <p className="text-stone-400 text-sm mb-6">
            This is the public address where clients will book sessions and view their galleries.
          </p>

          <label className="block">
            <span className="block text-[11px] uppercase tracking-[0.22em] text-stone-500 mb-2">Studio slug</span>
            <input
              type="text"
              value={slug}
              onChange={(e) => setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ""))}
              placeholder="jane-smith-photo"
              maxLength={40}
              data-testid="onboard-slug-input"
              className="w-full bg-white/[0.03] border border-white/[0.12] px-4 py-3 text-lg text-stone-100 focus:outline-none focus:border-[#C9A961] transition-colors placeholder:text-stone-600"
            />
          </label>

          <div className="mt-3 text-sm">
            <span className="text-stone-500">Your booking site:</span>{" "}
            <span className="font-mono text-stone-200" data-testid="onboard-slug-preview">{slugUrl}</span>
          </div>

          <p className="text-xs text-stone-500 mt-4">
            3–40 chars · lowercase letters, digits, hyphens · no leading/trailing hyphen
          </p>

          <div className="flex items-center justify-between mt-10">
            <button onClick={() => setStep(0)} className="text-xs uppercase tracking-[0.22em] text-stone-500 hover:text-stone-200 transition-colors">← Back</button>
            <button
              onClick={() => setStep(2)}
              disabled={!slugLooksValid}
              data-testid="onboard-slug-next-btn"
              className="inline-flex items-center gap-2 h-11 px-6 text-xs uppercase tracking-[0.22em] text-stone-900 font-medium hover:opacity-90 disabled:opacity-40 transition-opacity"
              style={{ background: `linear-gradient(135deg, ${GOLD}, ${GOLD_LIGHT})` }}
            >
              Continue <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </Frame>
    );
  }

  return (
    <Frame>
      <Stepper step={2} />
      <div className="max-w-xl mx-auto" data-testid="onboard-password-step">
        <h2
          className="text-3xl font-light mb-2 text-stone-100"
          style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}
        >
          Almost there.
        </h2>
        <p className="text-stone-400 text-sm mb-6">
          Set a password so you can log back into your Studio Manager any time.
        </p>

        <label className="block mb-4">
          <span className="block text-[11px] uppercase tracking-[0.22em] text-stone-500 mb-2">Your name</span>
          <input
            type="text"
            value={ownerName}
            onChange={(e) => setOwnerName(e.target.value)}
            data-testid="onboard-name-input"
            className="w-full bg-white/[0.03] border border-white/[0.12] px-4 py-3 text-base text-stone-100 focus:outline-none focus:border-[#C9A961] transition-colors"
          />
        </label>

        <label className="block">
          <span className="block text-[11px] uppercase tracking-[0.22em] text-stone-500 mb-2">Email (cannot be changed)</span>
          <input
            type="email"
            value={invite.owner_email}
            disabled
            data-testid="onboard-email-disabled"
            className="w-full bg-white/[0.02] border border-white/[0.08] px-4 py-3 text-base text-stone-500"
          />
        </label>

        <label className="block mt-4">
          <span className="block text-[11px] uppercase tracking-[0.22em] text-stone-500 mb-2">Password (min 8 chars)</span>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Use 8+ characters"
            data-testid="onboard-password-input"
            className="w-full bg-white/[0.03] border border-white/[0.12] px-4 py-3 text-base text-stone-100 focus:outline-none focus:border-[#C9A961] transition-colors placeholder:text-stone-600"
          />
        </label>

        <div className="flex items-center justify-between mt-10">
          <button onClick={() => setStep(1)} className="text-xs uppercase tracking-[0.22em] text-stone-500 hover:text-stone-200 transition-colors">← Back</button>
          <button
            onClick={handleSubmit}
            disabled={submitting || password.length < 8}
            data-testid="onboard-finish-btn"
            className="inline-flex items-center gap-2 h-12 px-8 text-xs uppercase tracking-[0.22em] text-stone-900 font-medium hover:opacity-90 disabled:opacity-40 transition-opacity"
            style={{ background: `linear-gradient(135deg, ${GOLD}, ${GOLD_LIGHT})` }}
          >
            {submitting ? (<><Loader2 className="w-4 h-4 animate-spin" /> Creating studio…</>) : (<><CheckCircle2 className="w-4 h-4" /> Claim my studio</>)}
          </button>
        </div>
      </div>
    </Frame>
  );
}

function Frame({ children }) {
  return (
    <div
      className="min-h-screen bg-[#070709] text-stone-100"
      style={{ fontFamily: "ui-sans-serif, system-ui, -apple-system, sans-serif" }}
      data-testid="lumetier-onboard"
    >
      {/* Subtle gold radial glow */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div
          className="absolute -top-20 left-1/2 -translate-x-1/2 w-[900px] h-[600px]"
          style={{ background: `radial-gradient(ellipse, rgba(201,169,97,0.10) 0%, transparent 65%)` }}
        />
      </div>
      <div className="relative max-w-4xl mx-auto px-6 pt-10 pb-20">
        <Link to="/lumetier" className="inline-block mb-8">
          <LumetierLogo size={84} inverted accent={GOLD} />
        </Link>
        {children}
      </div>
    </div>
  );
}

function Stepper({ step }) {
  return (
    <div
      className="max-w-xl mx-auto mb-10 flex items-center gap-3 text-[11px] uppercase tracking-[0.22em] text-stone-500"
      data-testid="onboard-stepper"
    >
      <span className={step >= 1 ? "text-stone-100" : ""}>1. URL</span>
      <span className="w-8 h-px bg-white/[0.15]" />
      <span className={step >= 2 ? "text-stone-100" : ""}>2. Password</span>
      <span className="w-8 h-px bg-white/[0.15]" />
      <span className="text-stone-700">3. Done</span>
    </div>
  );
}
